<template>
    <div class="app-steps-view">
        <div v-show="loading === true" class="app-steps-view__loader container-fluid">
            <svg class="app-steps-view__loader-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
                <path opacity=".1" d="M14 0h4v8h-4z">
                    <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0"/>
                </path>
                <path opacity=".1" d="M25.9 3.272l2.828 2.83-5.657 5.655-2.827-2.828z">
                    <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.125s"/>
                </path>
                <path opacity=".1" d="M32 14v4h-8v-4z">
                    <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.25s"/>
                </path>
                <path opacity=".1" d="M28.728 25.9l-2.83 2.828-5.655-5.657 2.828-2.827z">
                    <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.375s"/>
                </path>
                <path opacity=".1" d="M18 32h-4v-8h4z">
                    <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.5s"/>
                </path>
                <path opacity=".1" d="M6.1 28.728l-2.828-2.83 5.657-5.655 2.827 2.828z">
                    <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.675s"/>
                </path>
                <path opacity=".1" d="M0 18v-4h8v4z">
                    <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.75s"/>
                </path>
                <path opacity=".1" d="M3.272 6.1l2.83-2.828 5.655 5.657-2.828 2.827z">
                    <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.875s"/>
                </path>
            </svg>
        </div>
        <not-found v-if="error === true" />
        <product v-if="productVisible" />
        <price-calculation v-if="priceCalculationVisible" />
        <product-options v-if="productOptionsVisible" />
        <!-- v-show required to enable scroll behavior -->
        <personal-data v-show="personalDataVisible" />
        <checkout v-show="checkoutVisible" />
    </div>
</template>

<script>
import { mapState } from 'vuex'
import NotFound from '@/views/NotFound.vue'
import scrollBehavior, { scrollBehaviorPositionProcessor } from '../router/scrollBehavior'

const [, languageCode, countryCode] = process.env.VUE_APP_I18N_LOCALE.match(/([a-z]{2})(?:-([a-z]{2}))?$/i)
const isGermany = countryCode === 'DE' || (languageCode === 'de' && !countryCode)
const conditionalLocaleProduct = isGermany ? 'DE/' : ''
const conditionalLocalePriceCalculation = isGermany ? 'DE/' : ''
// ProductOptions only for non-german countries
const conditionalLocaleProductOptions = isGermany ? 'DE/' : ''
const conditionalLocalePersonalData = (function () {
    if (countryCode === 'BE') return 'BE/'
    return ['it', 'nl'].includes(languageCode) ? languageCode + '/' : ''
})()
const conditionalLocaleCheckout = (function () {
    if (isGermany) return 'DE/'
    return ['be', 'it', 'nl'].includes(languageCode) ? languageCode + '/' : ''
})()

const Product = require(`@/views/${conditionalLocaleProduct}Product`).default
const PriceCalculation = require(`@/views/${conditionalLocalePriceCalculation}PriceCalculation`).default
const ProductOptions = require(`@/views/${conditionalLocaleProductOptions}ProductOptions`).default

const components = {
    NotFound,
    Product,
    PriceCalculation,
    ProductOptions,
    PersonalData: () => import(/* webpackChunkName: "conditional-views" */ `@/views/${conditionalLocalePersonalData}PersonalData`),
    Checkout: () => import(/* webpackChunkName: "conditional-views" */ `@/views/${conditionalLocaleCheckout}Checkout`)
}

export default {
    name: 'Steps',
    components,
    data: () => ({
        loading: false,
        error: false
    }),
    computed: {
        ...mapState({
            productVisible: (state) => state.currentStep >= 0 && state.productUid !== null,
            priceCalculationVisible: (state) => state.currentStep >= 1,
            productOptionsVisible: (state) => state.currentStep >= 2,
            personalDataVisible: (state) => state.currentStep >= 3,
            checkoutVisible: (state) => state.currentStep >= 4
        })
    },
    created () {
        this.fetchProduct()
    },
    watch: {
        $route () {
            //only fetch if the product path segment changed
            if (this.$route.params && this.$route.params.productPathSegment !== this.$store.state.productPathSegment) {
                this.fetchProduct()
            }
        }
    },
    methods: {
        fetchProduct () {
            this.loading = true
            this.error = false
            this.$store.dispatch('setProduct', { productPathSegment: this.$route.params.productPathSegment })
                .then(() => {
                    const hash = this.$route.hash.slice(1)

                    if (hash === 'price-calculation') {
                        this.$store.commit('setCurrentStep', 1)
                        this.$nextTick(() => {
                            scrollBehavior(this.$route)
                                .then(scrollBehaviorPositionProcessor)
                                .then((position) => {
                                    window.scrollTo(position.x, position.y)
                                })
                        })
                    } else {
                        if (hash === '') return
                        this.$router.replace({
                            name: 'steps',
                            hash: '',
                            params: {
                                productPathSegment: this.$store.state.productPathSegment
                            }
                        })
                    }
                })
                .catch(() => {
                    //offer button to return to upper navigation level or history.back() ?
                    this.error = true
                })
                .finally(() => {
                    this.loading = false
                })
        }
    }
}
</script>

<style scoped>
    .app-steps-view__loader {
        position: relative;
        max-width: 1200px;
        text-align: center;
        margin-top: 1em;
        margin-bottom: 1em;
    }
</style>
